import { useTranslation } from 'react-i18next';
import {
  Button,
  FormRow,
  Input,
  Modal,
  Select,
  Text
} from '@efast_public/fjd-component-library';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import {
  CreateServiceTemplateResponse,
  ServiceTemplate,
  CreateServiceTemplateRequest,
  TemplateServiceType
} from '../api/govos.types';
import {
  GovosApiRoutes,
  govosClient,
  invalidateServiceTemplatesCache
} from '../api/govos-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../shared/notification/useNotification';

interface CreateServiceTemplateFormData {
  name: string;
}

interface CreateServiceTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: TemplateServiceType;
  templates: ServiceTemplate[];
  startAppId: number;
  existingNames?: string[];
  onSuccess: (name: string, template?: ServiceTemplate) => void;
}

export function CreateServiceTemplateModal({
  isOpen,
  onClose,
  serviceType,
  templates,
  startAppId,
  existingNames,
  onSuccess
}: CreateServiceTemplateModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const CustomJoi = createJoiWithTranslations(t);

  const validationSchema = CustomJoi.object<CreateServiceTemplateFormData>({
    name: CustomJoi.string()
      .required()
      .custom((value, helpers) => {
        if (
          existingNames
            ?.map((n) => n.toLowerCase())
            .includes(value.toLowerCase())
        ) {
          return helpers.error('name.duplicate');
        }
        return value;
      })
      .messages({
        'name.duplicate': t(
          'CreateServiceTemplateModal.validation.nameDuplicate'
        )
      })
  });

  const { register, handleSubmit, formState } =
    useForm<CreateServiceTemplateFormData>({
      defaultValues: { name: '' },
      resolver: joiResolver(validationSchema),
      mode: 'onBlur'
    });

  const { mutate: createServiceTemplate } = useMutation({
    mutationFn: async (payload: CreateServiceTemplateRequest) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(
            GovosApiRoutes.serviceTemplates(startAppId),
            payload
          ),
        {
          successMessage: t('api.createServiceTemplate.successMessage'),
          errorMessage: t('api.createServiceTemplate.errorMessage')
          // loadingMessage: t('api.createServiceTemplate.loadingMessage')
        }
      ),
    onSuccess: (data) => {
      const template = data as unknown as CreateServiceTemplateResponse;
      onSuccess?.('', template.template);
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
    }
  });

  const [selectedTemplate, setSelectedTemplate] = useState<
    ServiceTemplate | undefined
  >();

  const templateOptions = templates.map((template) => ({
    value: String(template.id),
    label: template.name
  }));

  const handleCreate = handleSubmit((data) => {
    createServiceTemplate({
      name: data.name,
      serviceType,
      configuration: selectedTemplate?.configuration
    });
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t(`CreateServiceTemplateModal.title.${serviceType}`)}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <FormRow
            label={t('CreateServiceTemplateModal.fields.name')}
            validationMessage={formState.errors.name?.message}
            variant={formState.errors.name ? 'error' : undefined}
          >
            <Input
              {...register('name')}
              placeholder={t(
                'CreateServiceTemplateModal.fields.namePlaceholder'
              )}
              style={{ width: '100%' }}
            />
          </FormRow>
          <FormRow
            label={t(`CreateServiceTemplateModal.description.${serviceType}`)}
          >
            <Select
              onChange={(value) => {
                if (value.length === 0) {
                  setSelectedTemplate(undefined);
                  return;
                }
                const selected = templates.find(
                  (tpl) => String(tpl.id) === value.at(0)?.value
                );
                setSelectedTemplate(selected);
              }}
              options={templateOptions}
              placeholder={
                templateOptions.length === 0
                  ? t('select.noOptionsAvailable')
                  : t('CreateServiceTemplateModal.fields.selectPlaceholder')
              }
              disabled={templateOptions.length === 0}
              style={{ width: '100%' }}
            />
          </FormRow>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={() => void handleCreate()}
            disabled={!formState.isValid}
            style={{ width: '100%' }}
          >
            {t('CreateServiceTemplateModal.actions.create')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            style={{ width: '100%' }}
          >
            {t('CreateServiceTemplateModal.actions.cancel')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
